import { defineStore } from "pinia";
import {
    SeatmapAreaSwitchedEvent,
    SeatmapErrorEvent,
    SeatmapZoomChange,
    SeatmapZoomEvent,
    SelectableInfo,
    SeatmapClientDataViewModel,
    SeatmapState,
    SeatmapUtils,
    SeatmapValidator,
    TicketSelectionApplicationData,
    TicketTypeItem,
    usePromotionStore
} from "@/domain/ticket-selection";
import { ApiService } from "webshop-modules";
import { Promotion } from "webshop-api";
import { PriceCategory } from "@eventim/seatmap-client";

export const useSeatmapStore = defineStore('seatmapStore', {
    state: (): SeatmapState => ({
        cartItems: 0,
        hasTicketsAvailable: true,
        initialized: false,
        ready: false,
        errorOnUserInteraction: false,
        genericError: false,
        priceLevelFilter: [],
        selectedFilterId: null,
        maxTickets: 0,
        maxTicketsCart: null,
        maxTicketsOrder: 0,
        validation: {
            isValid: false,
            isSizeValid: true,
            isCartSizeValid: true,
            priceCategories: [],
            ticketTypes: [],
        },
        zoom: {
            step: 1,
            amount: 0,
            canDecrease: false,
            canIncrease: true
        },
        priceLevelList: [],
        structuredSelection: [],
        selection: {
            selectTicketType: null,
            removeSelection: null,
            submitSelection: false,
        },
        areaId: 0,
        selectionDisplay: false,
        tokenData: {
            token: '',
            expireDate: 0,
        },
        tokenRequestsCounter: 0,
        mismatchedCurrencies: false,
        totalSelectionAmount: 0,
        fees: [],
    }),

    getters: {
        hasGenericError: (state) => state.genericError,
        hasError: (state) => state.genericError || state.errorOnUserInteraction,
        getToken: (state) => state.tokenData.token,
        getAreaId: (state) => state.areaId,
        isReady: (state) => state.ready,
        getStructuredSelection: (state) => state.structuredSelection,
        getPriceLevelFilter: (state) => state.priceLevelFilter,
        hasErrorOnUserInteraction: (state) => state.errorOnUserInteraction,
        hasTicketNumberValidationNotice: (state) =>
            !state.validation.isSizeValid || !state.validation.isCartSizeValid,
        hasInvalidSize: (state) => !state.validation.isSizeValid,
        hasInvalidCartSize: (state) => !state.validation.isCartSizeValid,
        removeSelectionAction: (state) => state.selection.removeSelection,
        selectTicketTypeAction: (state) => state.selection.selectTicketType,
        submitAction: (state) => state.selection.submitSelection,
        hasMismatchedCurrencies: (state) => state.mismatchedCurrencies,
    },

    actions: {
        updateState(state: SeatmapState) {
            this.$state = state;
        },
        maxCartRestriction(): number {
            const maxCartState = this.$state.maxTicketsCart;
            const itemsState = this.$state.cartItems;
            const maxOrderState = this.$state.maxTicketsOrder;

            const maxOrder = maxOrderState - itemsState >= 0 ? maxOrderState - itemsState : 0;
            const maxCart = maxCartState !== null ? (maxCartState - itemsState >= 0 ? maxCartState - itemsState : 0) : maxOrder;

            return maxCart < maxOrder ? maxCart : maxOrder;
        },
        setMismatchedCurrencyTo(val: boolean) {
            const state = {...this.$state};
            state.mismatchedCurrencies = val;

            this.updateState(state);
        },
        setRemovedSelection(selection: SelectableInfo) {
            const state = {...this.$state};
            state.selection.removeSelection = selection;

            this.updateState(state);
        },
        setSelectedTicketType(active: TicketTypeItem, selectionItem: SelectableInfo) {
            const state = {...this.$state};
            state.selection.selectTicketType = { active, selectionItem };

            this.updateState(state);
        },
        setSubmitSelectionTo(val: boolean) {
            const state = {...this.$state};
            state.selection.submitSelection = val;

            this.updateState(state);
        },
        setTotalSelectionAmount() {
            const state = {...this.$state};
            let total = 0;

            state.structuredSelection.map(pricelevel => {
                total += pricelevel.selectionInfo.ticketTypes.reduce((sel, cur) => {
                    if (!sel) {
                        return cur;
                    }

                    if (sel.active) {
                        return sel;
                    }

                    if (cur.active) {
                        return cur;
                    }

                    return sel.number < cur.number ? sel : cur;
                }).price;
            });

            state.totalSelectionAmount = total;
            this.updateState(state);
        },
        handleSeatmapZoom(event: SeatmapZoomEvent) {
            const state = {...this.$state};
            const zoom: SeatmapZoomChange = event.detail;

            state.zoom.canIncrease = zoom.max > zoom.current;
            state.zoom.canDecrease = zoom.current > zoom.min;
            state.zoom.amount = zoom.current;

            this.updateState(state);
        },
        changeSelectionDisplay(displayState: boolean) {
            const state = {...this.$state};
            state.selectionDisplay = displayState;

            this.updateState(state);
        },
        changeLayerLevel(data: { e: SeatmapAreaSwitchedEvent, toPrevious?: boolean }) {
            const state = {...this.$state};
            if (data.toPrevious) {
                state.areaId = data.e.detail.areaId;
            }

            this.updateState(state);
        },
        updateStructuredSelection(selectedTicket: SelectableInfo, activeTicketType: TicketTypeItem) {
            const state = {...this.$state};
            const priceLevel = state.structuredSelection.find(pricelevel =>
                pricelevel.selectionInfo.id === selectedTicket.id
                && pricelevel.selectionInfo.selectionDisplayPosition === selectedTicket.selectionDisplayPosition);

            if (!priceLevel) {
                return;
            }

            priceLevel.selectionInfo.ticketTypes = priceLevel.selectionInfo.ticketTypes.map(item => {
                item.active = item.priceId === activeTicketType.priceId;

                return item;
            });


            this.updateState(state);
            this.setTotalSelectionAmount();
        },
        toggleUserInteractionError(val: boolean) {
            const state = {...this.$state};
            state.errorOnUserInteraction = val;

            this.updateState(state);
        },
        async initializeStore(data: TicketSelectionApplicationData, api: ApiService) {
            await this.updateTDLToken(api);

            const state = {...this.$state};
            state.cartItems = data.cartAmount;
            state.maxTickets = data.eventInformation.maxTickets;
            state.maxTicketsCart = data.eventInformation.maxTicketsCart;
            state.maxTicketsOrder = data.priceList ? data.priceList.maxTicketsPerOrder! : data.eventInformation.maxTickets;
            state.initialized = true;
            state.structuredSelection = [];
            state.fees = data.feeList ?? [];

            if (data.priceList && data.priceList.priceLevels) {
                state.priceLevelList = data.priceList.priceLevels;
            }

            this.updateState(state);
            this.setTotalSelectionAmount();
            this.validateSelectionState(0);
        },
        async updateTDLToken(api: ApiService) {
            let state: SeatmapState;

            try {
                const response = await api.getTDLToken().then(res => res.authToken);
                state = {...this.$state};
                if (state.tokenData.token === response && state.tokenRequestsCounter !== 0) {
                    state.genericError = true;
                }

                state.tokenRequestsCounter = state.errorOnUserInteraction ? 1 : state.tokenRequestsCounter + 1;
                state.tokenData = SeatmapUtils.mapToTokenDataFrom(response);

                await this.updateState(state);
            } catch (e) {
                state = {...this.$state};
                state.genericError = true;

                await this.updateState(state);
            }
        },
        async resetStore() {
            const state = {...this.$state};
            state.initialized = true;
            state.structuredSelection = [];

            this.updateState(state);
        },
        async selectSeatmapItems(clientData: SeatmapClientDataViewModel) {
            const state = {...this.$state}
            const promotionStore = usePromotionStore();
            state.structuredSelection = await SeatmapUtils.selectionByPriceLevels(
                state,
                clientData,
                state.priceLevelList,
                <Promotion>promotionStore.activePromotion,
                state.maxTickets,
                state.maxTicketsOrder
            );

            const numberOfTickets = clientData.selectedTickets.reduce((prev, next) => prev + next.amount, 0);


            this.updateState(state);
            await this.validateSelectionState(numberOfTickets);
            this.setTotalSelectionAmount();
        },
        async validateSelectionState(selectedTicketsNumber: number) {
            const state = {...this.$state};

            if (!state.structuredSelection.length) {
                state.validation.isValid = false;

                return this.updateState(state);
            }

            state.validation.isSizeValid = SeatmapValidator.isSizeValidFor(state.maxTickets, selectedTicketsNumber);
            state.validation.isCartSizeValid = SeatmapValidator.isCartSizeValidFor(state, selectedTicketsNumber);
            state.validation.priceCategories = SeatmapValidator.arePriceCategoriesValidFor(state);
            state.validation.ticketTypes = SeatmapValidator.areTicketTypesValidFor(state);
            state.validation.isValid = state.validation.isSizeValid
                && state.validation.isCartSizeValid
                && state.validation.priceCategories.length === 0
                && state.validation.ticketTypes.length === 0;

            this.updateState(state);
        },
        async handleSeatmapReady(priceCategories: PriceCategory[], ticketAvailability: boolean) {
            const state = {...this.$state};
            const promotionStore = usePromotionStore();

            state.ready = true;
            state.hasTicketsAvailable = ticketAvailability;
            state.priceLevelFilter = SeatmapUtils.buildPriceLevelFilterStateFor(
                priceCategories,
                state.priceLevelList,
                <Promotion>promotionStore.activePromotion,
            );

            this.updateState(state);
        },
        async handleSeatmapError(e: SeatmapErrorEvent, api: ApiService) {
            if (
                SeatmapValidator.hasTokenValidityIssue(e)
                && !this.$state.errorOnUserInteraction
                && this.$state.tokenRequestsCounter < 2) {
                await this.toggleSeatmapReady(false);
                await this.updateTDLToken(api);
            } else {
                await this.toggleSeatmapError(true);
            }
        },
        async toggleSeatmapReady(val: boolean) {
            const state = {...this.$state};
            state.ready = val;

            this.updateState(state);
        },
        async toggleSeatmapError(val: boolean) {
            const state = {...this.$state};
            state.genericError = val;

            this.updateState(state);
        },
        async resetTokenRefetchCounter() {
            const state = {...this.$state};
            state.tokenRequestsCounter = 0;

            await this.updateState(state);
        }
    }
});
